import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Textarea,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useCreateSupplierStatementApi, useSearchBnplFacilitiesApi } from 'apis/bnpl';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface UploadSupplierStatementModalProps {
  trigger: JSX.Element;
}

type TFacilityOption = { value: number; label: string; restaurantId?: number };

interface FormInputs {
  facility: TFacilityOption | null;
  statementRef: string;
  statementAmount: string;
  periodStart: string;
  periodEnd: string;
  notes: string;
}

const defaultValues: FormInputs = {
  facility: null,
  statementRef: '',
  statementAmount: '',
  periodStart: '',
  periodEnd: '',
  notes: '',
};

export const UploadSupplierStatementModal = ({ trigger }: UploadSupplierStatementModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate, isLoading } = useCreateSupplierStatementApi();
  const { loadOptions: loadFacilityOptions } = useSearchBnplFacilitiesApi();
  const [file, setFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: { ...defaultValues },
  });

  const onCloseModal = () => {
    reset(defaultValues);
    setFile(null);
    onClose();
  };

  const onSubmit = (data: FormInputs) => {
    if (!file || !data.facility) return;

    const body = new FormData();
    body.append('file', file);
    body.append('facilityId', String(data.facility.value));
    if (data.statementRef) body.append('statementRef', data.statementRef);
    body.append('statementAmount', data.statementAmount);
    body.append('periodStart', data.periodStart);
    body.append('periodEnd', data.periodEnd);
    if (data.notes) body.append('notes', data.notes);

    mutate({ body }, { onSuccess: () => onCloseModal() });
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='lg'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Upload Supplier Statement</ModalHeader>
          <ModalBody>
            <Stack as='form' id='uploadSupplierStatementForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <FormControl isRequired>
                <FormLabel>Document (PDF or image)</FormLabel>
                <Input
                  type='file'
                  accept='.pdf,.jpg,.jpeg,.png'
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  p='xs'
                />
              </FormControl>

              <Controller
                control={control}
                name='facility'
                rules={{ required: 'Credit facility is required.' }}
                render={({ field: { onChange, onBlur, value, name }, fieldState: { error } }) => (
                  <FormControl isRequired isInvalid={!!error}>
                    <FormLabel>Credit Facility</FormLabel>
                    <ChakraAsyncSelect
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value}
                      name={name}
                      loadOptions={loadFacilityOptions}
                      placeholder='Type to search active facilities...'
                      isClearable
                      noOptionsMessage={({ inputValue }: { inputValue: string }) =>
                        inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No active facilities found'
                      }
                    />
                    <FormErrorMessage>{error?.message}</FormErrorMessage>
                  </FormControl>
                )}
              />

              <FormControl>
                <FormLabel>Statement Reference (optional)</FormLabel>
                <Input {...register('statementRef')} placeholder='External reference number' />
              </FormControl>

              <FormControl isInvalid={!!errors.statementAmount} isRequired>
                <FormLabel>Amount (AED)</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  min={0.01}
                  {...register('statementAmount', {
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than 0' },
                    validate: (value) => !isNaN(parseFloat(value)) || 'Please enter a valid number',
                  })}
                />
                <FormErrorMessage>{errors.statementAmount?.message}</FormErrorMessage>
              </FormControl>

              <HStack spacing='md'>
                <FormControl isInvalid={!!errors.periodStart} isRequired flex={1}>
                  <FormLabel>Period Start</FormLabel>
                  <Input type='date' {...register('periodStart', { required: 'Period start is required' })} />
                  <FormErrorMessage>{errors.periodStart?.message}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.periodEnd} isRequired flex={1}>
                  <FormLabel>Period End</FormLabel>
                  <Input type='date' {...register('periodEnd', { required: 'Period end is required' })} />
                  <FormErrorMessage>{errors.periodEnd?.message}</FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Notes (optional)</FormLabel>
                <Textarea {...register('notes')} placeholder='Optional notes' />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button
                variant='solid'
                colorScheme='red'
                type='submit'
                form='uploadSupplierStatementForm'
                isLoading={isLoading}
                isDisabled={!file}>
                Upload
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
